import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { setDeliveryLocation } from "../../../../Redux/delivery-location/slice";
import {
  getAllAddresses,
  setDefaultAddress,
} from "../../../../Redux/customer-address/slice";
import { useAuth } from "../../../../context/AuthContext";
import { Address } from "../../../../Redux/customer-address/types";

// Map country name → ISO 2-letter code for flag display
const COUNTRY_CODE_MAP: Record<string, string> = {
  India: "IN",
  "United States": "US",
  "United Kingdom": "GB",
  Canada: "CA",
  Australia: "AU",
  Germany: "DE",
  France: "FR",
  UAE: "AE",
  Singapore: "SG",
  // Add more as needed
};

const getCountryCode = (country: string): string =>
  COUNTRY_CODE_MAP[country] ?? country?.slice(0, 2).toUpperCase() ?? "??";

/** Flag image from flagcdn.com with a globe-emoji fallback */
const FlagImage = ({
  countryCode,
  size = 22,
}: {
  countryCode: string;
  size?: number;
}) => {
  const [error, setError] = useState(false);
  const code = countryCode.toLowerCase();

  if (error || !code || code === "??") {
    // Fallback: globe emoji in a neutral pill
    return (
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light border"
        style={{ width: size, height: size, fontSize: size * 0.6, lineHeight: 1 }}
        title="Unknown country"
      >
        🌐
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      width={size}
      height={size * 0.67}
      alt={countryCode}
      title={countryCode}
      className="rounded-1 object-fit-cover"
      style={{ objectFit: "cover" }}
      onError={() => setError(true)}
    />
  );
};

const Delivery = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const { location } = useAppSelector((state) => state.deliveryLocation);
  const { addresses, loading } = useAppSelector(
    (state) => state.customerAddress
  );

  // Fetch addresses when the dropdown is opened (only once per mount if authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getAllAddresses());
    }
  }, [dispatch, isAuthenticated]);

  // Derive the display text: country code of the selected/default address, or fallback
  const defaultAddr = addresses?.find((a) => a.defaultAddress);
  const displayCountryCode = defaultAddr
    ? getCountryCode(defaultAddr.country)
    : location?.state?.slice(0, 2).toUpperCase() ?? null;

  const displayText = displayCountryCode ?? "Set location";

  // The sub-label: city / pincode of default address or location
  const subLabel = defaultAddr
    ? `${defaultAddr.city}, ${defaultAddr.pincode}`
    : location?.city ?? location?.pincode ?? "Select address";

  const handleSelectDefault = (addr: Address) => {
    if (!addr._id) return;
    dispatch(setDefaultAddress(addr._id)).then(() => {
      dispatch(getAllAddresses());
    });
    // Also sync delivery location state
    dispatch(
      setDeliveryLocation({
        label: addr.addressType ?? "Home",
        area: addr.addressLine1,
        city: addr.city,
        state: addr.state,
        pincode: addr.pincode,
        displayName: `${addr.city}, ${addr.pincode}`,
        latitude: addr.latitude,
        longitude: addr.longitude,
      })
    );
  };

  // Don't render at all for unauthenticated users
  if (!isAuthenticated) return null;

  return (
    <>
      <div className="d-flex justify-content-start align-items-center gap-2 me-3 mt-n1 dropdown">
        {/* Trigger button */}
        <div
          className="cursor-pointer"
          id="dropdownMenuButton"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <div className="lh-0">
            <b className="text-black fw-normal text-nowrap text-xxs letter-spacing-1">
              Deliver to
            </b>
            <div className="d-flex align-items-center gap-1 mt-1">
              <FlagImage countryCode={displayText} size={22} />
              <b className="text-black fw-bolder text-sm">{displayText}</b>
              <i className="ri-arrow-drop-down-line text-black fs-6 ms-n1"></i>
            </div>
          </div>
        </div>

        {/* Dropdown panel */}
        <div
          className="dropdown-menu dropdown-menu-animation dropdown-menu-two border p-2 px-3 border-radius-lg ms-n8"
          aria-labelledby="dropdownMenuButton"
          style={{ minWidth: "300px" }}
        >
          <div className="p-2">
            <b className="text-black fw-bolder fs-6">Deliver to</b>
            <div className="mt-1 mb-3">
              <span className="text-black fw-normal text-xs text-nowrap">
                Select or manage your delivery address
              </span>
            </div>

            {/* Address List */}
            {loading ? (
              <div className="text-center py-2">
                <div
                  className="spinner-border spinner-border-sm text-dark"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : addresses && addresses.length > 0 ? (
              <ul className="list-unstyled mb-0">
                {addresses.map((addr) => (
                  <li
                    key={addr._id}
                    className={`d-flex align-items-start gap-2 p-2 mb-1 rounded cursor-pointer ${
                      addr.defaultAddress
                        ? "bg-light border border-dark"
                        : "border border-transparent"
                    }`}
                    style={{ transition: "background 0.15s" }}
                    onClick={() => handleSelectDefault(addr)}
                  >
                    {/* Radio-style indicator */}
                    <div className="mt-1 flex-shrink-0">
                      <div
                        className={`rounded-circle border border-2 ${
                          addr.defaultAddress
                            ? "border-dark bg-dark"
                            : "border-secondary"
                        }`}
                        style={{ width: 14, height: 14 }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-1">
                        <b className="text-black text-xs fw-bolder">
                          {addr.fullName}
                        </b>
                        <span
                          className="badge bg-secondary text-white text-xxs fw-normal"
                          style={{ fontSize: "0.6rem" }}
                        >
                          {addr.addressType}
                        </span>
                        {addr.defaultAddress && (
                          <span
                            className="badge bg-dark text-white text-xxs"
                            style={{ fontSize: "0.6rem" }}
                          >
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-secondary mt-1 text-nowrap text-truncate" style={{ maxWidth: 220 }}>
                        {addr.addressLine1}
                        {addr.addressLine2 ? `, ${addr.addressLine2}` : ""},{" "}
                        {addr.city}, {addr.state} — {addr.pincode}
                      </div>
                      <div className="text-xs text-muted">
                        {getCountryCode(addr.country)} · {addr.country}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center text-secondary text-xs py-2">
                No addresses saved yet.
              </div>
            )}

            <hr className="my-2" />

            {/* Add Address → navigate to ManageAddress */}
            <button
              className="btn btn-sm rounded-pill btn-dark w-100 fw-bolder"
              onClick={() => navigate("/ManageAddress")}
            >
              <i className="ri-add-line me-1"></i>
              Add address
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Delivery;